import { CardTransactionType } from 'core/api';
import { Level } from 'core/infrastructure';
import { Settings } from './Settings';
import { CardTransactionMetadata } from './CardTransactionMetadata';
import * as UserActivityRegistry from './UserActivityRegistry';

let initialized = false;

function exec<T>(mode: 'NO_REGISTER_USER_ACTIVITY' | 'REGISTER_USER_ACTIVITY', action: string, ...params: any[]): Promise<T> {

	const execPromise = new Promise<T>((resolve, reject) => {

		if (isNotCordovaNavigator()) {
			reject('Cordova is not injected');
			return;
		}

		window.Cordova.exec(
			(response: T) => { resolve(response); },
			error => { reject(error); },
			'Platform',
			action,
			params || []
		);
	});

	return mode === 'REGISTER_USER_ACTIVITY' ? UserActivityRegistry.registerWhile<T>(() => execPromise) : execPromise;
}

function initialize(): Promise<void> {

	return new Promise((resolve, reject) => {
		if (isNotCordovaNavigator()) {
			reject();
		}
		else {
			setTimeout(() => {
				if (!initialized)
					reject('Device is not present');
			}, 5000);
		}

		const document = window.document;
		if (document) {
			document.addEventListener('deviceready', async () => {
				initialized = true;
				resolve();
			}, false);
		}
	});
}

export function isInitialized() {
	return initialized;
}

export function isNotCordovaNavigator() {
	return !window.navigator || window.navigator.userAgent.indexOf('Cordova') === -1;
}

export function isCordovaNavigator() {
	return !isNotCordovaNavigator();
}

export async function configure(configuration: { UseCardProcessor: boolean, XmlCardProcessorSettings: string }, settings: Settings): Promise<void> {

	await initialize();

	await exec('NO_REGISTER_USER_ACTIVITY', 'log.startRemoteLog', `${document.location.origin}/log/`, settings.posId.toString(), settings.logLevel);

	if (configuration.UseCardProcessor) {
		// Hago un await y espero a que acabe. Prefiero esperar y sacarle un mensaje desde fuera mientras. Al no esperar en ocasiones puede desparecer
		// el error que lance. Me ha pasado en ocasiones que justo me sale el mensaje, mientras me estaba logueando con un usuario y por
		// el stacj de dialogos se queda por debajo error y no se ve. Da una sensación rara de que se ha perdido el mensaje.
		await exec('NO_REGISTER_USER_ACTIVITY', 'pinpad.configure', configuration.XmlCardProcessorSettings);
	}
}

export async function exitApp(): Promise<void> {
	await exec('NO_REGISTER_USER_ACTIVITY', 'system.close');
}

export async function uploadFullLog(settings: Settings): Promise<void> {
	await exec('NO_REGISTER_USER_ACTIVITY', 'log.uploadFullLog', `${document.location.origin}/log/upload/`, settings.posId.toString());
}

export function getAppVersionName(): Promise<string> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'info.getAppVersionName');
}

export function getAppVersionCode(): Promise<string> {
	return exec<string>('NO_REGISTER_USER_ACTIVITY', 'info.getAppVersionCode');
}

export function getLogContent(): Promise<string> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'log.getContent');
}

export function writeToLog(level: number, message: string): Promise<void> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'log.write', level, message);
}

export function setLogLevel(level: Level): Promise<void> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'log.setLevel', level);
}

export function print(content: string): Promise<void> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'print.html', content);
}

export function localPrinterIsConnected(): Promise<boolean> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'print.isConnected');
}

export async function showCardProcessorAdvancedOptions(): Promise<void> {
	await exec('REGISTER_USER_ACTIVITY', 'pinpad.showAdvancedOptions');
}

export function processSale(transactionId: string, amount: number, metadata: CardTransactionMetadata): Promise<{ TransactionId: string, XmlDetails: string, TransactionType: CardTransactionType }> {
	return exec('REGISTER_USER_ACTIVITY', 'pinpad.pay', amount, metadata, transactionId);
}

export function processUnreferencedRefund(transactionId: string, amount: number, metadata: CardTransactionMetadata): Promise<{ TransactionId: string, XmlDetails: string, TransactionType: CardTransactionType }> {
	return exec('REGISTER_USER_ACTIVITY', 'pinpad.unreferencedRefund', amount, metadata, transactionId);
}

export function processRefund(amount: number, metadata: CardTransactionMetadata): Promise<{ TransactionId: string, XmlDetails: string, TransactionType: CardTransactionType }> {
	return exec('REGISTER_USER_ACTIVITY', 'pinpad.refund', amount, metadata);
}

export function getNotificationToken(): Promise<string> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'notification.getToken');
}

export function showSettings(): Promise<void> {
	return exec('REGISTER_USER_ACTIVITY', 'system.showSettings');
}

export function openUrl(url: string): Promise<void> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'browser.openUrl', url);
}

export function writeFile(fileName: string, content: string): Promise<void> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'fileSystem.writeFile', fileName, content);
}

export function openQRScanner(): Promise<void> {
	return exec('REGISTER_USER_ACTIVITY', 'scanner.open');
}

export function scanQRCode(): Promise<string> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'scanner.scan');
}

export function cancelQRScan(): Promise<void> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'scanner.cancelScan');
}

export function closeQRScanner(): Promise<void> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'scanner.close');
}

export function shouldIgnoreLicenseCheck(): Promise<boolean> {
	return exec('NO_REGISTER_USER_ACTIVITY', 'system.shouldIgnoreLicenseCheck');
}


